"use client";

import { useEffect, useRef, useState, type ReactNode, type ElementType } from "react";
import { cn } from "@/lib/utils";

/**
 * Scroll-triggered reveal wrapper using IntersectionObserver.
 * Starts visible (no opacity:0) so the page renders correctly on first paint;
 * animation runs once the element enters the viewport.
 */
export function Reveal({
  children,
  className,
  delay = 0,
  as: Tag = "div",
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  as?: ElementType;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // If user prefers reduced motion, just stay visible
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }
    // perf(forge 2026-09-11): elements already on (or entering) the viewport
    // at mount NEVER hide — the old code flipped every Reveal to hidden on
    // hydration and waited for the IO/800ms failsafe, so the whole first
    // screen (100+ nodes) blinked invisible right as it should paint, driving
    // style+layout churn that held LCP/FCP back ~1-2s under mobile simulate.
    if (el.getBoundingClientRect().top < window.innerHeight * 1.2) {
      setVisible(true);
      return;
    }
    // Set initial hidden state on the client only
    setVisible(false);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            window.setTimeout(() => setVisible(true), delay);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(el);
    // Failsafe: if observer never fires (section already in view, etc.), show after 800ms
    const fallback = window.setTimeout(() => setVisible(true), 800);
    return () => {
      io.disconnect();
      window.clearTimeout(fallback);
    };
  }, [delay]);

  return (
    <Tag
      ref={ref as never}
      className={cn("reveal", visible && "is-visible", className)}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}
